import { type ComponentPropsWithRef } from 'react';
import { BookImage, Folder as FolderIcon } from 'lucide-react';
import { TruncatedText } from '@/components/ui/truncated-text';
import { type ChildNodeType, DAMNodeType } from '@/types/tree';
import { cn } from '@/lib/utils';

/**
 * The shared folder/album chip. Purely presentational: the browser view's leading section and
 * the home page's favorites each wrap it with their own context menu and click behaviour, so
 * this owns only the look. Replaces NodeThumbnail's tile presentation (DM-2517).
 *
 * Every other button prop passes straight through, which is what lets a Radix `asChild` trigger
 * wrap it — a closed prop surface silently swallowed the trigger's handlers, so right-clicking a
 * chip fell through to the browser's own menu.
 *
 * `type` and `aria-pressed` are the exceptions, and they are `Omit`ted rather than merely ignored:
 * both are set after the spread below, so accepting them would take a caller's value and silently
 * drop it. The node's own type is `nodeType` for the same reason — `type` belongs to the button.
 */
export type NodeChipProps = Omit<ComponentPropsWithRef<'button'>, 'type' | 'aria-pressed'> & {
  name: string;
  nodeType: ChildNodeType;
  selected?: boolean;
  /** Drop-target hover affordance, driven by the consumer's useDroppable. */
  isOver?: boolean;
  dataCy?: string;
};

export const NodeChip = ({ name, nodeType, selected, isOver, dataCy, className, ...rest }: NodeChipProps) => {
  const isAlbum = nodeType === DAMNodeType.Album;
  const Icon = isAlbum ? BookImage : FolderIcon;

  return (
    <button
      {...rest}
      type="button"
      data-cy={dataCy}
      // After the spread: a Radix `asChild` trigger also sets `data-state` (open/closed) and would
      // otherwise overwrite the selected state. Nothing here styles off Radix's value, and this
      // matches how Slot resolves a DOM child, where the child's own props win.
      data-state={selected ? 'selected' : undefined}
      aria-pressed={selected}
      className={cn(
        'flex max-w-64 cursor-pointer items-center gap-x-2 rounded-full border-2 border-transparent px-3 py-1.5 text-sm transition-colors duration-150',
        isAlbum ? 'bg-album-background-color' : 'bg-folder-background-color',
        // A ring here would double up the same node-type color the drag-over ring below already
        // uses. Selection instead recolors the border the chip already resets to transparent, same
        // language as everywhere else selection shows as a border/background change rather than a
        // ring. The ring stays for the drag-over affordance, matching the row view's own isOver
        // treatment.
        //
        // border-2, not border: list view marks a selected child twice over — a checked checkbox
        // and a row background — while a chip has only this. At 1px the difference between a
        // transparent and a highlight border on an already-tinted pill was a colour-only signal
        // thin enough to miss, so the two views disagreed on how loudly the same state reads.
        //
        // Colored per node type rather than always blue: the selected border echoes the chip's own
        // fill (folder-color blue, album-color green), the same pairing the icon already uses.
        selected && (isAlbum ? 'border-album-color' : 'border-folder-color'),
        isOver && (isAlbum ? 'ring-2 ring-inset ring-album-color' : 'ring-2 ring-inset ring-folder-color'),
        // `ring-ui-separator` (this app's usual gray focus ring) only reaches ~1.6-1.9:1 contrast
        // here against the chip's own tinted fill, short of WCAG 2.2's 3:1 minimum for a focus
        // indicator — every pre-existing site using that token is a form control that already
        // carries a resting `ring-1 ring-ui-bevel`, so the token only ever has to carry a
        // bevel-to-separator delta, not the whole signal from nothing. `ring-highlight-base` (the
        // app's blue) passes comfortably in both themes, so the sidebar tree's own blue focus ring
        // turns out to already be this convention, not an exception to it. `outline-hidden`, not
        // `outline-none`: `ring-*` compiles to `box-shadow`,
        // which forced-colors mode discards, so the native outline needs to stay in the DOM
        // (visually hidden) as that mode's fallback rather than being removed outright.
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-highlight-base',
        className,
      )}
    >
      {/* The tinted color lives on the icon only — putting it on the name too read as washed-out
          and low-contrast against the tinted background. The name is bold and full-contrast
          instead — a deliberate choice, not a match for either reference the reviewer offered:
          the sidebar tree's own folder name is default weight (folder.tsx), and the table/list
          view's Name column is font-medium (tile) / font-semibold (list), not bold
          (text-column.tsx). */}
      <Icon className={cn('size-4 shrink-0', isAlbum ? 'text-album-color' : 'text-folder-color')} strokeWidth={2} />
      {/* Chips cap at max-w-64, so a long name ellipsizes and the tooltip is the only way to
          read it in full. TruncatedText is the repo's primitive for exactly that pairing. */}
      <TruncatedText text={name} className="text-left font-bold text-text-base" />
    </button>
  );
};
